import FallBack from 'Components/Fallback'
import { Redirect, Route, Switch } from 'Core'
import CheckFeatureFlag from 'Views/CheckFeatureFlag'
import NotFound from 'Views/NotFound'
import React, { Suspense } from 'react'
import { lazyWithRetry } from 'Core/lazyWithRetry'
import { useUser } from 'Core/Hooks/useUser'

// Feature routes are lazy-loaded so each feature ships in its own chunk and
// is fetched only when its route is first visited.
const ApplicationRoutes = lazyWithRetry(() =>
  import('features/applications').then((m) => ({ default: m.ApplicationRoutes }))
)
const AgentApprovalRoutes = lazyWithRetry(() =>
  import('features/agentApproval').then((m) => ({ default: m.AgentApprovalRoutes }))
)
const ApprovalListRoutes = lazyWithRetry(() =>
  import('features/approvals').then((m) => ({ default: m.ApprovalListRoutes }))
)
const IAMResourcesRoutes = lazyWithRetry(() =>
  import('features/iamResources').then((m) => ({ default: m.IAMResourcesRoutes }))
)
const PolicyRoutes = lazyWithRetry(() =>
  import('features/policy').then((m) => ({ default: m.PolicyRoutes }))
)
const SessionRoutes = lazyWithRetry(() =>
  import('features/sessions').then((m) => ({ default: m.SessionRoutes }))
)
const TargetsRoutes = lazyWithRetry(() =>
  import('features/targets').then((m) => ({ default: m.TargetsRoutes }))
)
const ADDomainControllersUser = lazyWithRetry(() =>
  import('features/targets').then((m) => ({ default: m.ADDomainControllersUser }))
)
const ActivityLogsRoute = lazyWithRetry(() =>
  import('features/activityLogs').then((m) => ({ default: m.ActivityLogsRoute }))
)
const IAMRolesRoutes = lazyWithRetry(() =>
  import('features/iamRoles').then((m) => ({ default: m.IAMRolesRoutes }))
)
const BundlesRoutes = lazyWithRetry(() =>
  import('features/bundles').then((m) => ({ default: m.BundlesRoutes }))
)
const DataStoreRoutes = lazyWithRetry(() =>
  import('features/snowflake').then((m) => ({ default: m.DataStoreRoutes }))
)
const DevicePassKeys = lazyWithRetry(() =>
  import('features/settings').then((m) => ({ default: m.DevicePassKeys }))
)
const ServiceAccounts = lazyWithRetry(() =>
  import('features/servieAccounts').then((m) => ({ default: m.ServiceAccounts }))
)
const SecretsRoutes = lazyWithRetry(() =>
  import('features/secrets').then((m) => ({ default: m.SecretsRoutes }))
)
const UserVaultRoutes = lazyWithRetry(() =>
  import('features/userVault').then((m) => ({ default: m.UserVaultRoutes }))
)
const CloudAgentsRoutes = lazyWithRetry(() =>
  import('features/cloudAgents').then((m) => ({ default: m.CloudAgentsRoutes }))
)
const TeamDetails = lazyWithRetry(() =>
  import('features/github').then((m) => ({ default: m.TeamDetails }))
)

const UserDevices = lazyWithRetry(() => import('Views/UsersDevices'))
const DeviceDetails = lazyWithRetry(() => import('Views/DeviceList/DeviceDetails'))
const IAMActionDetailsWithContext = lazyWithRetry(
  () => import('Views/IamActionsAndResources/IAMActionList/IAMActionDetails')
)
const IAMActionAndResources = lazyWithRetry(() => import('Views/IamActionsAndResources'))
// TODO! Two route files ate not needed
const UserviewRoutes = () => {
  const view = 'user'
  const { currentUserIsAdmin, isAuditor } = useUser()
  return (
    <Suspense fallback={<FallBack />}>
      <Switch>
        {/* Root Redirects */}
        <Route exact path='/user'>
          <Redirect to='/user/targets' />
        </Route>

        <Route exact path='/user/dashboard'>
          <Redirect to='/user/targets' />
        </Route>

        <Route exact path={[`/${view}/index.html`]}>
          <Redirect to={`/${view}/targets`} />
        </Route>
        <Route exact path={[`/${view}/secrets`, `/${view}/secrets/*`]} component={SecretsRoutes} />
        <Route exact path={[`/${view}/my-vault`, `/${view}/my-vault/*`]} component={UserVaultRoutes} />
        <Route exact path={[`/${view}/bundles`, `/${view}/bundles/*`]} component={BundlesRoutes} />
        <Route
          exact
          path={[`/${view}/passkeys`, `/${view}/passkeys/*`]}
          component={DevicePassKeys}
        />
        <Route
          exact
          path={[`/${view}/activity-logs`, `/${view}/activity-logs/*`]}
          component={ActivityLogsRoute}
        />
        <Route exact path={[`/${view}/targets`, `/${view}/targets/*`]} component={TargetsRoutes} />
        <Route
          exact
          path={[`/${view}/resources`, `/${view}/resources/*`]}
          component={IAMResourcesRoutes}
        />
        <Route
          exact
          path={[
            `/${view}/policys`,
            `/${view}/policys/*`,
            `/${view}/policies`,
            `/${view}/policies/*`
          ]}
          component={PolicyRoutes}
        />
        <Route
          exact
          path={[
            `/${view}/applications`,
            `/${view}/applications/*`,
            `/${view}/saas-apps`,
            `/${view}/saas-apps/*`,
            `/${view}/agentic-apps`,
            `/${view}/agentic-apps/*`
          ]}
          component={ApplicationRoutes}
        />
        <Route
          exact
          path={[`/${view}/workload-agents`, `/${view}/workload-agents/*`]}
          component={CloudAgentsRoutes}
        />
        <Route
          exact
          path={[`/${view}/datacloud`, `/${view}/datacloud/*`]}
          component={DataStoreRoutes}
        />
        <Route
          exact
          path={[`/${view}/access-requests`, `/${view}/access-requests/*`]}
          component={ApprovalListRoutes}
        />
        <Route
          path={[`/${view}/agent-approvals`, `/${view}/agent-approvals/*`]}
          component={AgentApprovalRoutes}
        />
        <Route
          exact
          path={[`/${view}/session-logs`, `/${view}/session-logs/*`]}
          render={(props) => (
            currentUserIsAdmin || isAuditor ? <SessionRoutes {...props} /> : <NotFound />
          )}
        />
        <Route exact path={[`/${view}/addomaincontrollers`]} component={ADDomainControllersUser} />
        <Route
          exact
          path={[`/${view}/iam-roles`, `/${view}/iam-roles/*`]}
          component={IAMRolesRoutes}
        />
        <Route
          exact
          path={[
            `/${view}/service-accounts`,
            `/${view}/service-accounts/:accountType`,
            `/${view}/service-accounts/:accountType/:action`
          ]}
        >
          <CheckFeatureFlag isRoute permission='showServiceAccountsPage'>
            <ServiceAccounts />
          </CheckFeatureFlag>
        </Route>
        <Route exact path={[`/${view}/iam-actions/:accountType`]}>
          <CheckFeatureFlag isRoute permission='showIamActions'>
            <IAMActionAndResources tab='iam-actions' />
          </CheckFeatureFlag>
        </Route>
        <Route exact path={[`/${view}/account-resources/:accountType`]}>
          <CheckFeatureFlag isRoute permission='showIamActions'>
            <IAMActionAndResources tab='account-resources' />
          </CheckFeatureFlag>
        </Route>
        <Route exact path={[`/${view}/iam-policy/:accountType`]}>
          <CheckFeatureFlag isRoute permission='showIamActions'>
            <IAMActionAndResources tab='iam-policy' />
          </CheckFeatureFlag>
        </Route>
        <Route exact path={[`/${view}/iam-action/:name`, `/${view}/iam-action/:name/:action`]}>
          <CheckFeatureFlag isRoute permission='showIamActions'>
            <IAMActionDetailsWithContext />
          </CheckFeatureFlag>
        </Route>
        <Route exact path={`/${view}/devices`}>
          <CheckFeatureFlag isRoute permission='showUsersDevicesPage'>
            <UserDevices />
          </CheckFeatureFlag>
        </Route>
        <Route exact path={`/${view}/device/:id`}>
          <CheckFeatureFlag isRoute permission='showUsersDevicesPage'>
            <DeviceDetails />
          </CheckFeatureFlag>
        </Route>
        <Route exact path={`/${view}/team-details/:teamName`}>
          <TeamDetails />
        </Route>
        <Route>
          <NotFound />
        </Route>
      </Switch>
    </Suspense>
  )
}

export default UserviewRoutes
